import { useState } from "react";
import { Link } from "react-router-dom";

import { Avatar } from "@/components/AccountMenu";
import { ErrorNote } from "@/components/AppShell";
import HouseholdShell from "@/components/HouseholdShell";
import { auth, globalApi, type Identity } from "@/lib/api";
import { formatDate, useAsync } from "@/lib/hooks";
import { useSession } from "@/lib/session";
import type { VesselSummary } from "@/types";

const TYPE_LABEL = { pool: "Pool", hot_tub: "Hot tub" } as const;

/**
 * The account, as distinct from any pool: who you are, how you sign in,
 * which bodies of water are yours, and the two ways out.
 */
export default function Account() {
  const { status } = useSession();
  const identities = useAsync<Identity[]>(() => auth.identities(), []);
  const vessels = useAsync<VesselSummary[]>(
    () => globalApi.get<VesselSummary[]>("/api/vessels").catch(() => []),
    [],
  );
  const user = status.user;
  if (!user) return null;
  const name = user.display_name?.trim() || user.email;

  return (
    <HouseholdShell title="Account">
      <div className="space-y-4">
        <section className="card flex items-center gap-4 p-4">
          <Avatar name={name} src={user.avatar_url} size={56} />
          <div className="min-w-0">
            <p className="truncate font-medium">{name}</p>
            <p className="truncate text-sm text-(--color-ink-muted)">{user.email}</p>
            {user.is_admin && <p className="unit mt-1">Site admin</p>}
          </div>
        </section>

        <section className="card p-4">
          <h2 className="text-sm font-semibold">How you sign in</h2>
          <p className="mt-1 text-xs text-(--color-ink-faint)">
            A second provider with the same verified address joins this account rather than
            creating another.
          </p>
          {identities.error && <ErrorNote message={identities.error} />}
          <ul className="mt-3 divide-y divide-(--color-border-subtle)">
            {(identities.data ?? []).map((identity) => (
              <li key={identity.provider} className="flex items-baseline justify-between gap-3 py-2 text-sm">
                <span>
                  <span className="font-medium">{identity.label}</span>
                  <span className="ml-2 text-(--color-ink-muted)">{identity.email}</span>
                </span>
                <span className="unit shrink-0">
                  {identity.last_used_at ? `used ${formatDate(identity.last_used_at)}` : "never used"}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="card p-4">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">Your pools</h2>
            <Link to="/add" className="text-sm text-(--color-brand)">
              Add one
            </Link>
          </div>
          <ul className="mt-2 divide-y divide-(--color-border-subtle)">
            {(vessels.data ?? []).map((vessel) => (
              <li key={vessel.id}>
                <Link
                  to={`/v/${vessel.id}`}
                  className="flex items-baseline justify-between gap-3 py-2 text-sm hover:text-(--color-brand)"
                >
                  <span className="truncate">{vessel.name}</span>
                  <span className="unit shrink-0">{TYPE_LABEL[vessel.vessel_type]}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <SignOutAndDelete />
      </div>
    </HouseholdShell>
  );
}

/**
 * Two taps to delete, with the consequence spelled out between them, and no
 * browser dialog: a `confirm()` reads as the app shrugging.
 */
function SignOutAndDelete() {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signOut() {
    await auth.logout();
    window.location.assign("/");
  }

  async function deleteAccount() {
    setDeleting(true);
    setError(null);
    try {
      await auth.deleteAccount();
      window.location.assign("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the account.");
      setDeleting(false);
    }
  }

  return (
    <section className="card space-y-3 p-4">
      <button type="button" onClick={() => void signOut()} className="btn btn-quiet w-full text-sm">
        Sign out
      </button>
      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="w-full text-xs text-(--color-ink-faint) underline"
        >
          Delete my account
        </button>
      ) : (
        <div className="rounded-xl bg-(--color-surface-sunken) p-3 text-sm">
          <p>
            This removes your account and, if you are its only member, every reading, dose,
            event, photo and conversation in it. There is no undo.
          </p>
          {error && <ErrorNote message={error} />}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => void deleteAccount()}
              disabled={deleting}
              className="btn flex-1 bg-(--color-status-high) text-white"
            >
              {deleting ? "Deleting…" : "Delete everything"}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className="btn btn-quiet flex-1">
              Keep it
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
